/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: sanitized HTML */
import React from 'react';

import DOMPurify from 'dompurify';

import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import type { HtmlView } from '@/components/ui/widget/views/html/html.type';
import type { TextView } from '@/components/ui/widget/views/text/text.view.type';
import type { WidgetData } from '@/components/ui/widget/widget.type';
import { getByPath } from '@/utils/widgets';

// separate the function that will create the html markup
const createMarkup = (
  htmlData,
  sanitizerConfig?: Parameters<typeof DOMPurify.sanitize>[1]
) => {
  const cleanHTML = DOMPurify.sanitize(htmlData.toString(), sanitizerConfig);
  return { __html: cleanHTML };
};

type Props = {
  view: HtmlView;
  data: WidgetData;
};

// view to inject sanitized html
const HTMLViewImpl = React.memo(({ view, data }: Props) => {
  const { path, default: defaultValue } = view;

  const htmlData = getByPath(data, path) || defaultValue;
  if (htmlData === undefined) return null;

  // sanitize and render HTML
  return (
    <div
      className="overflow-hidden text-ellipsis"
      dangerouslySetInnerHTML={createMarkup(htmlData)}
    />
  );
});

// sort defaults to text on path
const getSortConfig = (view: HtmlView): TextView => ({
  type: 'text',
  path: view.path
});

// filtering defaults to text search on path
const getFilterConfig = (view: HtmlView): TextView => ({
  type: 'text',
  path: view.path
});

const getDefaultWidth = (): ColumnWidth => 'xlarge';

/**
 * stringifies data at path
 * note: this html is NOT SANITIZED
 *
 * Sanitizing is down to the client that will consume it.
 * On our side, we only take care of the raw table data export.
 * The export is not meant to be rendered in html in any of our web pages.
 */

const getStringifyFunction = (view: HtmlView) => {
  const { path, default: defaultValue } = view;
  return data => getByPath(data, path) || defaultValue || '';
};

export const htmlViewProvider = {
  component: HTMLViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
